import { useState, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router';
import { styled, useTheme } from "@mui/material/styles";
import { Box, useMediaQuery } from '@mui/material';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import { ToolbarAccountOverride } from '../components/SidebarFooterAccount/SidebarFooterAccount';
import SidebarFooterAccount from '../components/SidebarFooterAccount/SidebarFooterAccount';
import Header from './Header/Header';
import PageHeader from './PageHeader';
import { useAppConfigState, useConfigState } from '../context/AppContext/AppContext';
import { Footer } from '../components/Footer/Footer';
import { useLayoutDispatch } from '../context/LayoutContext/LayoutContext';
import { ProgressIndicator } from '../components/ProgressIndicator/ProgressIndicator';
import { useBanners } from '../hooks/useBanner';
import Sidebar from './Sidebar';

const FooterContainer = styled("div")(({ theme }) => ({
    flexShrink: 0,
    padding: theme.spacing(0, 1.5),
    width: 'calc(100% - 20px)', // Leave space for scrollbar (adjust 20px as needed)
    maxWidth: 'calc(100vw - 20px)', // Ensure it doesn't exceed viewport width minus scrollbar
    position: 'fixed',
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    left: 0, // Ensure it starts from the left edge
    [theme.breakpoints.up('md')]: {
        left: '64px', // Account for mini sidebar width
        width: 'calc(100% - 64px - 20px)', // Subtract sidebar width and scrollbar space
        maxWidth: 'calc(100vw - 64px - 20px)',
    },
}));


export const ToolpadDashboardLayout = () => {
    const configState = useConfigState();
    const layoutDispatch = useLayoutDispatch();
    const { headerTitleInfo, isLoading } = useAppConfigState();
    const { getBannersByPriority } = useBanners();
    const sortedBanners = getBannersByPriority();

    const [isMiniMode, setIsMiniMode] = useState(false);
    const [isExpandIconVisible, setExpandIconVisible] = useState(false);

    const theme = useTheme();
    const extraSmall = useMediaQuery(theme.breakpoints.only('xs'));

    const isBreadcrumbVisible = !!headerTitleInfo && headerTitleInfo.linkParams?.parents;

    const isTitleHidden = headerTitleInfo?.titleHidden || false;
    const isSmallPadding = headerTitleInfo?.smallPadding || false;

    const dashboardLayoutStyles = useMemo(() => ({
        margin: '0px !important',
        padding: '0px !important',
        '& .MuiPaper-root': {
            paddingLeft: '0px !important',
        },
        '& .MuiContainer-root': {
            padding: '0px !important',
        },

        '& .MuiAppBar-root': {
            borderBottom: '#1c2220',
            // Add specific handling for md-lg range
            [theme.breakpoints.up('md')]: {
                left: '64px !important',
                width: 'calc(100% - 64px) !important', // Add this to ensure proper width
            },
            // Ensure it works for larger screens too
            [theme.breakpoints.up('lg')]: {
                left: '64px !important',
                width: 'calc(100% - 64px) !important',
            },
            '& .MuiToolbar-root': {
                margin: '0px !important',
            },
        },
        // Push the main content area
        '& main': {
            // paddingTop: '20px !important',
            marginLeft: '64px !important',  
            marginTop: '0px !important',
            padding: `4px ${isSmallPadding ? '0px' : '40px' } 40px ${isSmallPadding ? '0px' : '40px' } !important`,


            ...(sortedBanners.length > 0 ? {
                marginTop: `${(50 * sortedBanners.length) + 10}px !important`,
                [theme.breakpoints.down('md')]: {
                    marginLeft: '0px !important',
                    marginTop: `${(50 * sortedBanners.length) + 10 + (isBreadcrumbVisible ? 96 : 70)}px !important`,
                },
            } : {}),
            ...(sortedBanners.length === 0 ? {
                // Mobile/tablet: Account for 3-line header
                [theme.breakpoints.down('md')]: {
                    marginLeft: '0px !important',
                    marginTop: isBreadcrumbVisible ? '92px !important' : '70px !important',
                    padding: `${4 + (isBreadcrumbVisible ? 0 : 0)}px ${isSmallPadding ? '0px' : '40px' } 40px ${isSmallPadding ? '0px' : '40px' } !important`,
                },

            } : {}),


            '& .MuiStack-root': {
                margin: '0px !important',
                padding: '0px',
            },
            overflowX: 'hidden',
        },
    }), [theme, sortedBanners.length, isBreadcrumbVisible, isSmallPadding]);

    useEffect(() => {
        if (extraSmall) {
            layoutDispatch({ type: 'CLOSE_SIDEBAR' });
        }
        else if (isExpandIconVisible) {
            if (isMiniMode) {
                layoutDispatch({ type: 'CLOSE_SIDEBAR' });
            } else {
                layoutDispatch({ type: 'OPEN_SIDEBAR' });
            }
        } else if (!isMiniMode) {
            layoutDispatch({ type: 'OPEN_SIDEBAR' });
        }
    }, [extraSmall, isMiniMode, isExpandIconVisible, layoutDispatch]);

    useEffect(() => {
        const checkMiniMode = () => {
            // Get all elements with the class .MuiDrawer-root
            const drawerElements = document.querySelectorAll('.MuiDrawer-root');
            let visibleDrawerElement: HTMLElement | null = null;

            // Find the first drawer element that is not hidden
            for (let i = 0; i < drawerElements.length; i++) {
                const element = drawerElements[i] as HTMLElement;
                const computedStyle = window.getComputedStyle(element);
                if (computedStyle.display !== 'none') {
                    visibleDrawerElement = element;
                    break; // Found the visible drawer, no need to check further
                }
            }

            if (visibleDrawerElement) {
                // Get the computed style which reflects the actual rendered width
                const computedStyle = window.getComputedStyle(visibleDrawerElement);
                const width = computedStyle.width;
                // Set mini mode to true if the width is less than 100px
                // parseFloat will extract the number from "XXpx"
                setIsMiniMode(parseFloat(width) < 100);
            } else {
                console.log('Visible Drawer element not found.');
                setIsMiniMode(false);
            }
        };

        checkMiniMode(); // Initial check
        // Use MutationObserver to re-check when the drawer might appear or change
        const observer = new MutationObserver(checkMiniMode);
        // Observe the body for additions/removals, and the drawer itself for attribute changes (like style or class)
        // This is a broad observation, might need refinement based on how the drawer's width actually changes
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

        // Also listen for resize events, as layout changes might affect width
        window.addEventListener('resize', checkMiniMode);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', checkMiniMode);
        };
    }, []);

    // New useEffect for isMenuCollapsed
    useEffect(() => {
        const checkMenuCollapsed = () => {
            const expandButtonContainer = document.querySelector<HTMLDivElement>(
                'header .MuiStack-root > .MuiStack-root:has(div[aria-label="Expand menu"]), header .MuiStack-root > .MuiStack-root:has(div[aria-label="Collapse menu"])'
            );

            if (expandButtonContainer?.hasChildNodes() && expandButtonContainer?.childNodes.length > 1) {
                const firstChild = expandButtonContainer.childNodes[0] as HTMLElement;
                const secondChild = expandButtonContainer.childNodes[1] as HTMLElement;
                const firstExpandDisplay = window.getComputedStyle(firstChild).display;
                const firstExpandLabel = firstChild.firstElementChild?.getAttribute('aria-label');
                const secondExpandDisplay = window.getComputedStyle(secondChild).display;
                const secondExpandLabel = secondChild.firstElementChild?.getAttribute('aria-label');

                // Modified condition to check that secondChild's first child has aria-label "Expand menu"
                const isExpandIcon = (firstExpandDisplay !== "none" && firstExpandLabel === "Expand menu") || (secondExpandDisplay !== "none" && secondExpandLabel === "Expand menu");
                setExpandIconVisible(isExpandIcon);

                // Find the visible button element (either expand or collapse)
                let visibleButton: Element | null = null;
                if (firstExpandDisplay !== "none" && firstChild.firstElementChild) {
                    visibleButton = firstChild.firstElementChild;
                } else if (secondExpandDisplay !== "none" && secondChild.firstElementChild) {
                    visibleButton = secondChild.firstElementChild;
                }

                // Find the SVG within the visible button and add the data-testid
                if (visibleButton) {
                    const svgElement = visibleButton.querySelector('svg');
                    if (svgElement) {
                        svgElement.setAttribute('data-testid', isExpandIcon ? 'MenuIcon' : 'MenuOpenIcon');
                    }
                }

            } else {
                setExpandIconVisible(false);
            }
        };

        checkMenuCollapsed(); // Initial check

        // Use a MutationObserver to re-check when DOM changes might affect button visibility
        // Observing attributes might be necessary if visibility is toggled via classes/styles directly on the button
        const observer = new MutationObserver(checkMenuCollapsed);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true, // Observe attribute changes which might affect styles
            attributeFilter: ['style', 'class'] // Be more specific if possible
        });

        // Also listen for resize events, as layout changes might affect visibility
        window.addEventListener('resize', checkMenuCollapsed);

        return () => {
            observer.disconnect(); // Cleanup observer
            window.removeEventListener('resize', checkMenuCollapsed); // Cleanup resize listener
        };
    }, []);

    return (
        <DashboardLayout
            defaultSidebarCollapsed={true}
            disableCollapsibleSidebar
            isMenuCollapsed={true}
            hideNavigation
            slots={{
                toolbarAccount: ToolbarAccountOverride,
                sidebarFooter: SidebarFooterAccount,
                appTitle: Header,
            }}
            sx={dashboardLayoutStyles}
        >
            <Sidebar />

            <PageContainer
                maxWidth={false}
                title=""
                breadcrumbs={[]}
                data-testid="page-container"
            >
                {isLoading && <ProgressIndicator />}

                {headerTitleInfo && !isTitleHidden && <PageHeader {...headerTitleInfo} />}

                <Outlet />
                {!configState.dashboardMode && (
                    <>
                        <Box sx={{ height: '50px', width: '100%' }} />
                        <FooterContainer>
                            <Footer />
                        </FooterContainer>
                    </>
                )}
            </PageContainer>
        </DashboardLayout>
    );
};